/**
 * Creative state (senders, texts, per-drop, per-country) for multi-country bulk edit.
 */

import { useCallback, useState } from 'react';

import {
  filterSenderNamesMax11,
  MAX_SELECTED_TEXTS,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

export function useMultiCountryCreativeState() {
  const [selectedSenderNames, setSelectedSenderNames] = useState<string[]>([]);
  const [selectedSmsTexts, setSelectedSmsTexts] = useState<string[]>([]);
  const [perDropSmsTexts, setPerDropSmsTexts] = useState<string[][] | null>(null);
  const [perDropSenderNames, setPerDropSenderNames] = useState<string[][] | null>(null);
  const [perCountryTexts, setPerCountryTexts] = useState<Record<string, string[]>>({});
  const [perCountrySenders, setPerCountrySenders] = useState<Record<string, string[]>>({});

  const toggleSender = useCallback((name: string) => {
    setSelectedSenderNames((prev) =>
      prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name],
    );
  }, []);

  const toggleText = useCallback((text: string) => {
    setSelectedSmsTexts((prev) =>
      prev.includes(text)
        ? prev.filter((v) => v !== text)
        : prev.length >= MAX_SELECTED_TEXTS ? prev : [...prev, text],
    );
  }, []);

  const clearSendersSelection = useCallback(() => setSelectedSenderNames([]), []);
  const clearTextsSelection = useCallback(() => setSelectedSmsTexts([]), []);

  const saveGeneratedAndSelectOnly = useCallback((texts: string[]) => {
    setSelectedSmsTexts(texts.slice(0, MAX_SELECTED_TEXTS));
    setPerDropSmsTexts(null);
  }, []);

  const saveGeneratedSendersAndSelectOnly = useCallback((senders: string[]) => {
    setSelectedSenderNames(senders.slice(0, 50));
    setPerDropSenderNames(null);
  }, []);

  const setCountryTexts = useCallback((ck: string, texts: string[]) => {
    setPerCountryTexts((prev) => ({ ...prev, [ck]: texts }));
  }, []);

  const setCountrySenders = useCallback((ck: string, senders: string[]) => {
    setPerCountrySenders((prev) => ({ ...prev, [ck]: senders }));
  }, []);

  const toggleCountryText = useCallback((ck: string, text: string) => {
    setPerCountryTexts((prev) => {
      const list = prev[ck] ?? [];
      return { ...prev, [ck]: list.includes(text) ? list.filter((t) => t !== text) : [...list, text] };
    });
  }, []);

  const toggleCountrySender = useCallback((ck: string, name: string) => {
    setPerCountrySenders((prev) => {
      const list = prev[ck] ?? [];
      return { ...prev, [ck]: list.includes(name) ? list.filter((n) => n !== name) : [...list, name] };
    });
  }, []);

  const saveGeneratedTextsPerDropMergeOrder = useCallback((batches: string[][]) => {
    setPerDropSmsTexts(batches);
    setPerCountryTexts({});
  }, []);

  const saveGeneratedSendersPerDropMergeOrder = useCallback((batches: string[][]) => {
    setPerDropSenderNames(batches);
    setPerCountrySenders({});
  }, []);

  const saveGeneratedTextsByCountry = useCallback((byCountry: Record<string, string[]>) => {
    setPerDropSmsTexts(null);
    setPerCountryTexts((prev) => {
      const next = { ...prev };
      for (const [ck, texts] of Object.entries(byCountry)) next[ck] = texts;
      return next;
    });
  }, []);

  const saveGeneratedSendersByCountry = useCallback((byCountry: Record<string, string[]>) => {
    setPerDropSenderNames(null);
    setPerCountrySenders((prev) => {
      const next = { ...prev };
      for (const [ck, senders] of Object.entries(byCountry)) next[ck] = senders;
      return next;
    });
  }, []);

  const buildCreativePayload = useCallback(() => {
    const result: {
      senderNames?: string[];
      smsTexts?: string[];
      smsTextsPerDrop?: string[][];
      senderNamesPerDrop?: string[][];
      smsTextsByCountry?: Record<string, string[]>;
      senderNamesByCountry?: Record<string, string[]>;
    } = {};
    const hasPerCountryTexts = Object.values(perCountryTexts).some((v) => v.length > 0);
    const hasPerCountrySenders = Object.values(perCountrySenders).some((v) => v.length > 0);

    if (hasPerCountrySenders) {
      result.senderNamesByCountry = perCountrySenders;
    } else if (perDropSenderNames?.length) {
      result.senderNamesPerDrop = perDropSenderNames;
    } else if (selectedSenderNames.length > 0) {
      result.senderNames = filterSenderNamesMax11(uniqueKeepOrderStrings(selectedSenderNames)).slice(0, 50);
    }

    if (hasPerCountryTexts) {
      result.smsTextsByCountry = perCountryTexts;
    } else if (perDropSmsTexts?.length) {
      result.smsTextsPerDrop = perDropSmsTexts;
    } else if (selectedSmsTexts.length > 0) {
      result.smsTexts = uniqueKeepOrderStrings(selectedSmsTexts).slice(0, 50);
    }
    return result;
  }, [selectedSenderNames, selectedSmsTexts, perDropSmsTexts, perDropSenderNames, perCountryTexts, perCountrySenders]);

  return {
    selectedSenderNames, setSelectedSenderNames,
    selectedSmsTexts, setSelectedSmsTexts,
    perDropSmsTexts, setPerDropSmsTexts,
    perDropSenderNames, setPerDropSenderNames,
    perCountryTexts, perCountrySenders,
    toggleSender, toggleText,
    clearSendersSelection, clearTextsSelection,
    saveGeneratedAndSelectOnly, saveGeneratedSendersAndSelectOnly,
    setCountryTexts, setCountrySenders,
    toggleCountryText, toggleCountrySender,
    saveGeneratedTextsByCountry,
    saveGeneratedSendersByCountry,
    saveGeneratedTextsPerDropMergeOrder,
    saveGeneratedSendersPerDropMergeOrder,
    buildCreativePayload,
  };
}
